import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Permissions } from '@app/core/guard/Permissions';
import { AttachmentTypeRequest } from '@app/core/models/attachment-type/attachment-type';
import { ErrorResponse } from '@app/core/models/commom/error-response';
import { VaultBaseComponent } from '@app/shared/components/base/vault-base.component';
import { AppState } from '@app/state';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { FormErrorHandlerService } from '@services/helpers/form-error-handler.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { UtilsService } from '@services/helpers/utils.service';
import { AttachmentTypeService } from '@services/http/attachment-type.service';
import { nameof } from '@src/app/core/utils/nameof.utils';
import { MenuItem } from 'primeng/api';

@Component({
  templateUrl: './attachment-type-register.component.html',
  styleUrls: ['./attachment-type-register.component.scss'],
  standalone: false,
})
export class AttachmentTypeRegisterComponent extends VaultBaseComponent implements OnInit {
  defaultLanguage: string = 'pt-BR';
  attachmentTypeId: string = '';
  breadcrumbs: MenuItem[] = [];
  home: MenuItem = {
    icon: 'pi pi-home',
    routerLink: '/dashboard/dashboard-management',
    label: this.t(this.tk.General.Dashboard),
  };
  attachmentTypePermissions = Permissions.DerivativeAttachmentType;
  public attachmentTypeRegisterProps = nameof<AttachmentTypeRequest>();

  attachmentTypeForm = this.formBuilder.group({
    [this.attachmentTypeRegisterProps.id]: [{ value: '', disabled: true }],
    [this.attachmentTypeRegisterProps.typeName]: ['', [Validators.required]],
    [this.attachmentTypeRegisterProps.description]: [''],
  });

  constructor(
    translateService: TranslateService,
    store: Store<AppState>,
    private formBuilder: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private utilsService: UtilsService,
    private formErrorHandler: FormErrorHandlerService,
    private toast: ToastHelperService,
    private attachmentTypeService: AttachmentTypeService
  ) {
    super(translateService, store);
    this.defaultLanguage = translateService.currentLang;
  }

  ngOnInit(): void {
    this.breadcrumbs = [
      { label: this.t(this.tk.General.Administration), routerLink: '/' },
      {
        label: this.t(this.tk.General.Groups),
        routerLink: '/dashboard/attachment-type/list',
      },
      {
        label: this.t(this.tk.General.Register),
        routerLink: '/dashboard/attachment-type/register',
      },
    ];

    this.getVerifyAttachmentTypeId();
  }

  getVerifyAttachmentTypeId(): void {
    this.route.params.subscribe(params => {
      if (params['id']) {
        this.attachmentTypeId = params['id'];
        this.utilsService.setIsLoading(true);
        this.attachmentTypeService.getAttachmentTypeById(this.attachmentTypeId).subscribe({
          next: response => {
            this.utilsService.setIsLoading(false);
            if (response) {
              const attachmentType = response;

              this.attachmentTypeForm.controls[this.attachmentTypeRegisterProps.id].setValue(
                attachmentType.id || ''
              );

              this.attachmentTypeForm.controls[this.attachmentTypeRegisterProps.typeName].setValue(
                attachmentType.typeName || ''
              );

              this.attachmentTypeForm.controls[
                this.attachmentTypeRegisterProps.description
              ].setValue(attachmentType.description || '');
            }
          },
          error: error => {
            this.utilsService.setIsLoading(false);
            console.log('getVerifyAttachmentTypeId error: ', error);

            this.toast.showToastError(
              this.t(this.tk.Management.ErrorLoadingAttachmentType),
              error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
            );
          },
        });
      }
    });
  }

  cancelRegister(): void {
    this.router.navigate(['dashboard/attachment-type/list']);
  }

  registerAttachmentType(): void {
    if (this.attachmentTypeForm.invalid) {
      this.attachmentTypeForm.markAllAsTouched();
      return;
    }

    const formValue = this.attachmentTypeForm.getRawValue() as unknown as AttachmentTypeRequest;

    const keysToSend = ['typeName', 'description'];
    Object.keys(formValue).forEach(key => {
      if (!keysToSend.includes(key)) {
        delete formValue[key as keyof AttachmentTypeRequest];
      }
    });

    if (this.attachmentTypeId) {
      this.putAttachmentType(formValue);
    } else {
      this.postAttachmentType(formValue);
    }
  }

  postAttachmentType(attachmentType: AttachmentTypeRequest): void {
    this.utilsService.setIsLoading(true);
    this.attachmentTypeService.postAttachmentType(attachmentType).subscribe({
      next: response => {
        this.utilsService.setIsLoading(false);
        if (response) {
          this.toast.showToastSuccess(
            this.t(this.tk.Management.AttachmentTypeCreatedSuccessfully),
            response?.message || ''
          );
          this.router.navigate(['dashboard/attachment-type/list']);
        }
      },
      error: error => {
        this.utilsService.setIsLoading(false);
        if (error instanceof ErrorResponse) {
          this.formErrorHandler.applyFormErrors(this.attachmentTypeForm, error);
        } else {
          console.log('postAttachmentType error: ', error);
        }
        this.toast.showToastError(
          this.t(this.tk.Management.ErrorCreatingAttachmentType),
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
    });
  }

  putAttachmentType(attachmentType: AttachmentTypeRequest): void {
    this.utilsService.setIsLoading(true);
    this.attachmentTypeService.putAttachmentType(attachmentType, this.attachmentTypeId).subscribe({
      next: response => {
        this.utilsService.setIsLoading(false);
        if (response) {
          this.toast.showToastSuccess(
            this.t(this.tk.Management.AttachmentTypeUpdatedSuccessfully),
            response?.message || ''
          );
          this.router.navigate(['dashboard/attachment-type/list']);
        }
      },
      error: error => {
        this.utilsService.setIsLoading(false);
        if (error instanceof ErrorResponse) {
          this.formErrorHandler.applyFormErrors(this.attachmentTypeForm, error);
        } else {
          console.log('putAttachmentType error: ', error);
        }
        this.toast.showToastError(
          this.t(this.tk.Management.ErrorUpdatingAttachmentType),
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
    });
  }
}
